import { defineStore } from 'pinia';
import {
  ESsPlayerFreespinStage,
  type ISsPlayerBonuses,
  type ISsPlayerFreespins,
} from '~/types/softswiss/ssPlayerBonuses';
import { COMMON_REQUEST_INTERVAL } from '~/utils/constants';
import { shouldShowBonusNotification } from '~/utils/notifications/notificationFilters';
import { softswiss } from '../transport/softswiss';
import { useStoreSsPlayer } from './player';

export type LoadBonusesOptions = {
  recreateStore?: boolean;
  force?: boolean;
};

export const useStoreSsPlayerBonuses = defineStore('ssPlayerBonuses', {
  state: () => ({
    bonuses: [] as ISsPlayerBonuses[],
    freespins: [] as ISsPlayerFreespins[],
    lastUpdateTime: {
      bonuses: NaN,
      freespins: NaN,
    },
    intervalId: NaN,
    allBonusesPromise: null as Promise<any> | null,
  }),

  getters: {
    /**
     * Getter that returns bonuses that player can use
     * @param state - store state
     * @returns handle bets bonuses
     */
    usableBonuses(state): ISsPlayerBonuses[] {
      return state.bonuses.filter((b) => b.stage === 'handle_bets');
    },

    /**
     * Getter that returns freespins bonuses that are available for activation
     * @param state - store state
     * @returns fs bonuses
     */
    usableFreespins(state): ISsPlayerFreespins[] {
      return state.freespins.filter((fs) => fs.stage === 'issued' || fs.stage === 'activated');
    },

    /**
     * Getter that returns amount of bonuses that are available for player
     * @param state - store state
     * @returns bonuses amount as number
     */
    activableBonusesAmount(state): number {
      return [...state.bonuses, ...state.freespins].filter((b) => b.stage === 'issued').length;
    },

    usableBonusesAndFreespins(state): Array<ISsPlayerBonuses | ISsPlayerFreespins> {
      return [...state.bonuses, ...state.freespins]
        .filter(
          (item) =>
            item.stage === 'activated' || item.stage === 'issued' || item.stage === 'handle_bets'
        )
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    },
    activeBonusesAndFreespins(): Array<ISsPlayerBonuses | ISsPlayerFreespins> {
      return this.usableBonusesAndFreespins.filter(
        (b) => b.stage === 'activated' || b.stage === 'handle_bets'
      );
    },
    activeRandomBonus(): Array<ISsPlayerBonuses | ISsPlayerFreespins> {
      return this.activeBonusesAndFreespins.find((b) => b.type === 'random');
    },
  },

  actions: {
    saveDataIfNeeded(payload: {
      newItems: ISsPlayerFreespins[] | ISsPlayerBonuses[];
      stateKey: 'bonuses' | 'freespins';
    }) {
      type BonusOrFs = ISsPlayerBonuses | ISsPlayerFreespins;
      const { newItems, stateKey } = payload;
      const oldArray = this[stateKey] as BonusOrFs[];

      const isCreatedLater = (a: BonusOrFs, b: BonusOrFs) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime() > 0;

      const newArr = [...oldArray];
      let shouldReCreateArr = false;

      newItems.forEach((newItem: BonusOrFs) => {
        const isExistingItem = oldArray.find((existingItem) => existingItem.id === newItem.id);

        if (isExistingItem) {
          return;
        }

        shouldReCreateArr = true;

        newArr.push(newItem);
        let i = newArr.length - 1;

        while (i > 0 && isCreatedLater(newItem, newArr[i - 1])) {
          newArr[i] = newArr[i - 1];
          i -= 1;
        }
        newArr[i] = newItem;
      });

      // update only if new entries came but also check if shouldn't set to []
      if (shouldReCreateArr || !newItems.length) {
        this[stateKey] = newArr as [];
      }

      this.lastUpdateTime[stateKey] = Date.now();
    },
    /**
     * Method for loading player's bonuses
     */
    async loadBonuses(options?: LoadBonusesOptions) {
      const storeSsPlayer = useStoreSsPlayer();
      if (!storeSsPlayer.isPlayerAuthorised) {
        return;
      }

      const bonuses: ISsPlayerBonuses[] | null = await softswiss().get({
        path: '/api/player/bonuses',
        force: options?.force,
      });

      if (!bonuses?.length) {
        return;
      }

      if (options?.recreateStore) {
        this.bonuses = bonuses;
        this.lastUpdateTime.bonuses = Date.now();
      } else {
        this.saveDataIfNeeded({
          newItems: bonuses,
          stateKey: 'bonuses',
        });
      }
    },

    /**
     * Method for loading player's freespin bonuses
     */
    async loadFreespinsBonuses(options?: LoadBonusesOptions) {
      const storeSsPlayer = useStoreSsPlayer();
      if (!storeSsPlayer.isPlayerAuthorised) {
        return;
      }

      const freespins: ISsPlayerFreespins[] | null = await softswiss().get({
        path: '/api/player/freespins',
        force: options?.force,
      });

      if (!freespins?.length) {
        return;
      }

      if (options?.recreateStore) {
        this.freespins = freespins;
        this.lastUpdateTime.freespins = Date.now();
      } else {
        this.saveDataIfNeeded({
          newItems: freespins,
          stateKey: 'freespins',
        });
      }
    },

    /**
     * Method for getting all player's bonuses data from SS
     */
    async loadAllBonuses(force = false) {
      if (!this.allBonusesPromise) {
        this.allBonusesPromise = Promise.all([
          this.loadBonuses({ force, recreateStore: force }),
          this.loadFreespinsBonuses({ force, recreateStore: force }),
        ]).finally(() => (this.allBonusesPromise = null));
      }
      await this.allBonusesPromise;
    },

    /**
     * Method for activating issued freespins
     * @fsId - freespin ID
     */
    async activateFreespins(fsId: number) {
      const storeSsPlayer = useStoreSsPlayer();
      if (!storeSsPlayer.isPlayerAuthorised) {
        return;
      }

      await softswiss().post({
        path: `/api/player/freespins/${fsId}/activation`,
      });
      await this.loadFreespinsBonuses({ force: true, recreateStore: true });
    },

    /**
     * Method for checking activation status of current freespin
     * @param data - freespin ID and number of status checking
     */
    async freespinActivationCheck(data: { freespinId: number; checkCount?: number }) {
      const sleep = (ms: number) => {
        return new Promise((resolve) => setTimeout(resolve, ms));
      };

      for (let tries = 0; tries < (data?.checkCount || 5); tries++) {
        await this.loadFreespinsBonuses({ force: true, recreateStore: true });
        if (!this.freespins.find((fs) => fs.id === data.freespinId)?.activatable) {
          break;
        } else {
          await sleep(3000);
        }
      }
    },

    /**
     * Method for checking activation status of current bonus
     * @param data - bonus ID and number of status checking
     */
    async bonusActivationCheck(data: { bonusId: number; checkCount?: number }) {
      const sleep = (ms: number) => {
        return new Promise((resolve) => setTimeout(resolve, ms));
      };

      for (let tries = 0; tries < (data?.checkCount || 5); tries++) {
        await this.loadBonuses({ force: true, recreateStore: true });
        if (!this.freespins.find((fs) => fs.id === data.bonusId)?.activatable) {
          break;
        } else {
          await sleep(3000);
        }
      }
    },

    /**
     * Method for activating issued bonuses
     * @bonusId - bonus ID
     */
    async activateBonuses(bonusId: number) {
      const storeSsPlayer = useStoreSsPlayer();
      if (!storeSsPlayer.isPlayerAuthorised) {
        return;
      }

      await softswiss().post({
        path: `/api/player/bonuses/${bonusId}/activation`,
      });
      await this.loadBonuses({ force: true, recreateStore: true });
    },

    /**
     * Method for canceling issued freespins
     * @fsId - freespin ID
     */
    async forfiteBonuses(fsId: number) {
      const storeSsPlayer = useStoreSsPlayer();
      if (!storeSsPlayer.isPlayerAuthorised) {
        return;
      }

      await softswiss().delete({
        path: `/api/player/bonuses/${fsId}`,
      });
      await this.loadBonuses({ force: true, recreateStore: true });

      const currBonus = this.bonuses.find((fs) => fs.id === fsId);

      // checks if bonus has active state
      if (currBonus && shouldShowBonusNotification(currBonus)) {
        const checkCanceling = window.setInterval(() => {
          const currStepBonusState = this.bonuses.find((fs) => fs.id === fsId);

          // checks if bonus has active state
          if (currStepBonusState && shouldShowBonusNotification(currStepBonusState)) {
            this.loadFreespinsBonuses({ force: true, recreateStore: true });
          } else {
            window.clearInterval(checkCanceling);
          }
        }, 5000);
      }
    },

    /**
     * Method for canceling issued freespins
     * @fsId - freespin ID
     */
    async forfiteFreespins(fsId: number) {
      const storeSsPlayer = useStoreSsPlayer();
      if (!storeSsPlayer.isPlayerAuthorised) {
        return;
      }

      await softswiss().delete({
        path: `/api/player/freespins/${fsId}`,
      });
      await this.loadFreespinsBonuses({ force: true, recreateStore: true });

      if (this.freespins.find((fs) => fs.id === fsId)?.stage === ESsPlayerFreespinStage.ACTIVATED) {
        const checkCanceling = window.setInterval(() => {
          if (
            !(
              this.freespins.find((fs) => fs.id === fsId)?.stage !==
              ESsPlayerFreespinStage.ACTIVATED
            )
          ) {
            this.loadFreespinsBonuses({ force: true, recreateStore: true });
          } else {
            window.clearInterval(checkCanceling);
          }
        }, 5000);
      }
    },

    async applyCoupon(coupon: string, params: { isReturnResponse?: boolean } = {}) {
      const storeSsPlayer = useStoreSsPlayer();
      if (!storeSsPlayer.isPlayerAuthorised) {
        return;
      }

      const response = await softswiss().post({
        path: `/api/bonuses/coupon`,
        payload: {
          coupon_code: coupon,
        },
      });
      if (params.isReturnResponse) {
        return response;
      }
      if (response.status < 300) {
        return true;
      }
      return false;
    },

    async checkIfCouponPresent() {
      const coupon = localStorage.getItem('7BIT_COUPON_CODE');
      if (coupon) {
        await this.applyCoupon(coupon);
        localStorage.removeItem('7BIT_COUPON_CODE');
      }
    },

    async startCheckingUpdates() {
      await this.loadAllBonuses(true);
      this.intervalId = window.setInterval(() => {
        if (Date.now() - this.lastUpdateTime.bonuses > COMMON_REQUEST_INTERVAL) {
          this.loadBonuses();
        } else if (Date.now() - this.lastUpdateTime.freespins > COMMON_REQUEST_INTERVAL) {
          this.loadFreespinsBonuses();
        }
      }, COMMON_REQUEST_INTERVAL);
    },

    stopCheckingUpdates() {
      window.clearInterval(this.intervalId);
      this.$reset();
    },
  },
});
